<template>
  <div class="uploadSection">
    <div
      class="upload-panel bg-surface-50"
      :class="props.elementClasses"
    >
      <div
        v-show="false"
        class="js-manuallyUpload"
        @click="() => { 
          upload()
        }"
      ></div>

      <div class="upload-panel__btn">
        <label class="fake-input-file">
          <input
            type="file"
            name="file"
            style="display: none;"
            :accept="props.accept"
            :disabled="props.disabled"
            @change="async (e) => {
              if (props.options?.manuallyUpload) {
                newFile = await e.target.files[0]

                if (!fileTitle && newFile.name) { 
                  fileTitle = newFile.name.split('.').slice(0, -1).join('.')
                }

                fileDetails()
              } else {
                newFile = await e.target.files[0]

                fileDetails()
                upload()
              }
            }"
          />
          <div
            class="btn btn-flat"
            :class="{ 'disabled': props.disabled }"
          >
            <span class="text">{{ $t('uploadFile') }}</span>
          </div>
        </label>
      </div>

      <div class="upload-panel__text">
        <div
          v-if="isProcess"
          class="progress"
        >
          <div class="indeterminate"></div>
        </div>
        <div
          v-if="!isProcess"
          class="text"
        >
          <div
            v-if="fileName"
            class="text"
          >
            {{ fileName }}
          </div>

          <div
            v-else
            class="text"
            v-html="props.placeholder ? props.placeholder : $t('uploadFile')"
          ></div>
        </div>
        <div
          v-if="fileName && !isProcess"
          class="right"
        >
          <small
            v-if="fileSize"
            class="size"
          >{{ fileSize }}</small>

          <a
            class="clear trashcan"
            @click="remove()"
          ><i class="icon icon-trash"></i></a>
        </div>
      </div>
    </div>

    <!-- <p
      v-if="props.disclaimer"
      class="small grey-text left-align"
      v-html="props.disclaimer"
    >
    </p> -->

    <div
      v-if="props.options?.showTitleField && props.options?.manuallyUpload"
      class="upload-panel-title"
    >
      <div class="input-field ">
        <label
          class="active"
        >{{ props.options.titleFieldLabel }}</label>
        
        <InputText
          v-model="fileTitle"
          :placeholder="props.options.titleFieldPlaceholder"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
const { $i18n: i18n } = useNuxtApp()
const { uploadFile, deleteFile, uploadVimeo } = useGeneral()

const fileName = ref(null)
const fileUrl = ref(null)
const fileSize = ref(null)
const isProcess = ref(false)

const newFile = ref(null)

const fileTitle = ref(null)

/**
* Emits
*/
const emit = defineEmits(['returnData', 'file'])

/**
* Props
*/
const props = defineProps({
  attachFile: {
    type: [String, Array, Number],
    default: ''
  },
  elementClasses: {
    type: [String],
    default: ''
  },
  placeholder: {
    type: [String],
    default: ''
  },
  disclaimer: {
    type: [String],
    default: ''
  },
  accept: {
    type: [String],
    default: ''
  },
  disabled: {
    type: [Boolean],
    default: false
  },
  options: {
    type: [Object],
    default: null
  },
  max: {
    type: [Number],
    default: null
  }
})

/**
* Format File Size
*/
function formatFileSize(bytes) {
  const kilobyte = 1024
  const megabyte = kilobyte * 1024

  if (bytes >= megabyte) {
    return (bytes / megabyte).toFixed(2) + ' MB'
  } else if (bytes >= kilobyte) {
    return (bytes / kilobyte).toFixed(2) + ' KB'
  } else {
    return bytes + ' B'
  }
}

/**
* File Details
*/
const fileDetails = (() => { 
  fileName.value = newFile.value.name
  if (fileName.value.length > 20) {
    fileName.value = fileName.value.substring(0, 10) + '...' + fileName.value.substring(fileName.value.length - 7, fileName.value.length)
  }
  fileSize.value = formatFileSize(newFile.value.size)
})

/**
* Upload File
*/
const upload = async () => {
  if (!newFile.value) {
    return false
  }

  let emitReturn = false

  isProcess.value = true

  const formData = new FormData()

  if (props.max) {
    if (newFile.value.size > props.max) { 
      emitReturn = {
        error: i18n?.t('uploadingSizeFailed', { maxSize: formatFileSize(props.max) })
      }
    }
  }

  /**
  * Uploading
  */
  if (!emitReturn) {
    if (props.options?.uploadType === 'vimeo') {
      formData.append('title', fileTitle.value ? fileTitle.value : newFile.value.name)
      formData.append('video', newFile.value)

      isProcess.value = false
      emitReturn = uploadVimeo(formData).then((res) => {
        if (res.data.video_info) {
          return {
            data: {
              video_info: res.data.video_info
            }
          }
        } else {
          return {
            error: res
          }
        }
      })
    } else {
      formData.append('file', newFile.value)

      emitReturn = uploadFile(formData).then((res) => {
        fileUrl.value = res.data?.main?.url

        isProcess.value = false
        return {
          data: res.data?.main?.url
        }
      }).catch(() => {
        isProcess.value = false

        return {
          data: ''
        }
      })
    }
  }

  /**
  * Return / Emit
  */
  if (!props.options?.manuallyUpload) {
    emit('file', emitReturn)
  }

  emit('returnData', emitReturn)
}

/**
* Remove Upload File
*/
const remove = () => {
  if (fileUrl.value) {
    const fileNameArray = fileUrl.value.split('/')
    const s3folder = fileNameArray[fileNameArray.length - 2]
    const fileKey = fileNameArray[fileNameArray.length - 1]

    deleteFile({
      s3folder,
      fileName: fileKey,
      uploadType: props.options?.uploadType 
    }).then(() => {
      emit('file', '')
    }).catch(() => {
      emit('file', '')
    })
  }

  fileTitle.value = ''
  newFile.value = ''
  fileName.value = ''
  fileSize.value = ''
  fileUrl.value = ''
}

/**
* Init
*/
onMounted(() => {
  if (props.attachFile?.length) {
    fileUrl.value = props.attachFile
    if (fileUrl.value) {
      const fileNameTmp = fileUrl.value.split('/')[fileUrl.value.split('/').length - 1]
      fileName.value = fileNameTmp.substring(0, 10) + '...' + fileNameTmp.substring(fileNameTmp.length - 7, fileNameTmp.length)
    }
  }
})
</script>

<style lang="scss" scoped>
.upload-panel {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin: 0;
  margin-top: 5px;
  background-color: color("grey", "lighten-6");
  border-radius: 5px;
  &__btn {
    flex: 1 auto;
    width: 100%;
    max-width: 160px;
    margin-right: 15px;
    margin-bottom: 0;
    label {
      width: 100%!important;
    }
    .btn {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      min-width: 100px !important;
      padding: 8px 20px;
      line-height: 1rem;
    }
  }
  &__text {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1 1 auto;
    overflow: hidden;
    min-width: 140px;
    font-size: 12px;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: inherit;
    opacity: .7;
    .right {
      display: flex;
      align-items: center;
    }
    .trashcan {
      cursor: pointer;
    }
  }
}
</style>
